import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Box,
  Layers,
  Package,
  ArrowLeftRight,
  BarChart3,
  Menu,
  X,
  Wallet,
  LogOut,
  ShieldCheck,
  ChevronDown,
  KeyRound,
} from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  logout,
  getUser,
  getRole,
  getCurrentProject,
  setCurrentProject,
} from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ChangePasswordDialog({ open, onClose, userId }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      // Verify current password by attempting login first
      const meRes = await axios.get(`${API}/auth/me`);
      await axios.post(`${API}/auth/login`, {
        username: meRes.data.username,
        password: currentPassword,
      });
      await axios.put(`${API}/users/${userId}/password`, { password: newPassword });
      toast.success("Password updated successfully");
      onClose();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Current password is incorrect");
      } else {
        toast.error(err.response?.data?.detail ?? "Failed to update password");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm uppercase tracking-wider text-zinc-300">
            Change Password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Current Password</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="bg-zinc-800 border-zinc-700 text-white rounded-none focus:border-orange-500"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className="bg-zinc-800 border-zinc-700 text-white rounded-none focus:border-orange-500"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Confirm New Password</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              className="bg-zinc-800 border-zinc-700 text-white rounded-none focus:border-orange-500"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-zinc-700 text-zinc-300">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="rounded-none bg-orange-500 hover:bg-orange-600">
              {loading ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const DATA_NAV_ITEMS = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/components", label: "Components", icon: Box },
  { path: "/sub-assemblies", label: "Sub-Assemblies", icon: Layers },
  { path: "/assemblies", label: "Assemblies", icon: Package },
  { path: "/inventory", label: "Inventory", icon: ArrowLeftRight },
  { path: "/reports", label: "Reports", icon: BarChart3 },
  { path: "/cash-advances", label: "Trésorerie", icon: Wallet },
];

const ROLE_BADGE_COLORS = {
  super_admin: "bg-red-900/60 text-red-300",
  project_admin: "bg-orange-900/60 text-orange-300",
  manager: "bg-blue-900/60 text-blue-300",
  viewer: "bg-zinc-700 text-zinc-300",
};

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [currentProjectId, setCurrentProjectIdState] = useState(getCurrentProject());
  const navigate = useNavigate();
  const user = getUser();
  const role = getRole();
  const [pwDialogOpen, setPwDialogOpen] = useState(false);

  const isAdmin = role === "super_admin" || role === "project_admin";

  useEffect(() => {
    axios.get(`${API}/projects`).then((res) => {
      setProjects(res.data);
    }).catch(() => { });
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const handleProjectChange = (e) => {
    const newId = e.target.value;
    setCurrentProject(newId);
    setCurrentProjectIdState(newId);
    window.location.reload();
  };

  const currentProjectName = projects.find((p) => p.id === currentProjectId)?.name ?? "—";

  return (
    <div className="app-container">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 md:hidden"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </Button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? "open" : ""}`} data-testid="sidebar">
        <div className="sidebar-header">
          <Package size={24} className="text-orange-500" />
          <span className="sidebar-logo">STOCKOS</span>
        </div>

        {/* Project selector */}
        {projects.length > 1 ? (
          <div className="px-4 pb-2">
            <div className="relative">
              <select
                value={currentProjectId ?? ""}
                onChange={handleProjectChange}
                className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 text-xs rounded-none px-2 py-1.5 pr-6 appearance-none focus:outline-none focus:border-orange-500"
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            </div>
          </div>
        ) : (
          <div className="px-4 pb-2">
            <p className="text-xs text-zinc-500 font-mono truncate">{currentProjectName}</p>
          </div>
        )}

        <nav className="sidebar-nav">
          {DATA_NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `nav-item ${isActive ? "active" : ""}`
              }
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <item.icon className="nav-item-icon" />
              <span>{item.label}</span>
            </NavLink>
          ))}

          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-admin"
            >
              <ShieldCheck className="nav-item-icon" />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="p-4 border-t border-zinc-800">
          {/* Username + role badge */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-zinc-300 font-mono truncate flex-1">
              {user?.username ?? "—"}
            </span>
            {role && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider ${ROLE_BADGE_COLORS[role] ?? "bg-zinc-700 text-zinc-300"}`}>
                {role.replace("_", " ")}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-none text-xs uppercase tracking-wider"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPwDialogOpen(true)}
            className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-none text-xs uppercase tracking-wider"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            Change Password
          </Button>
          <p className="text-xs text-zinc-600 font-mono mt-2">v2.0.0</p>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="main-content">
        <Outlet />
      </main>

      <ChangePasswordDialog
        open={pwDialogOpen}
        onClose={() => setPwDialogOpen(false)}
        userId={user?.id}
      />
    </div>
  );
}
